import { GetApiIntroducerPortalDirectorSearchResponse } from '@iwoca/lapi-client/introducer_portal';
import { Card, Input, Message, Select } from '@iwoca/orion';
import clsx from 'clsx';
import { FileUpload } from 'components/Registration/FileUpload/FileUpload';
import {
  DOCUMENT_UPLOAD_UK,
  wrapFileInArray,
} from 'components/Registration/FileUpload/FileUpload.helpers';
import { DirectorSearch } from 'components/Signup/NewSignupForm/DirectorSearch/DirectorSearch';
import { useFormContext } from 'react-hook-form';
import { PRIVACY_FIELD_CLASS } from 'utils/privacyClasses';
import { FieldError } from '../FieldError/FieldError';
import sharedStyles from '../RegistrationForm.module.css';
import { TRegistrationFormValues } from '../registrationForm.types';
import styles from './AccountInformation.module.css';

type TProps = {
  isCompanySearch: boolean;
  companyNumber?: string;
};

export const AccountInformation = ({
  isCompanySearch,
  companyNumber,
}: TProps) => {
  const {
    register,
    setValue,
    trigger,
    watch,
    formState: { isSubmitted, errors },
  } = useFormContext<TRegistrationFormValues>();

  const handleDirectorSearchSelect = (
    director?: GetApiIntroducerPortalDirectorSearchResponse['data'][number],
  ) => {
    setValue('firstName', director?.first_name || '');
    setValue('lastName', director?.last_name || '');

    if (isSubmitted) {
      trigger();
    }
  };

  return (
    <div>
      <h2 className={sharedStyles.heading}>Account information</h2>
      <Card
        className={clsx(
          sharedStyles.formFieldsContainer,
          styles.accountFormFieldsContainer,
        )}
      >
        <div className={(sharedStyles.formField, styles.titleContainer)}>
          <Select
            {...register('title')}
            label="Title"
            id="title"
            type="text"
            className={clsx(PRIVACY_FIELD_CLASS, {
              [sharedStyles.erroredField]: errors?.title?.message,
            })}
          >
            <option value="Mr">Mr</option>
            <option value="Ms">Ms</option>
            <option value="Mrs">Mrs</option>
            <option value="Miss">Miss</option>
          </Select>
          <FieldError errorMessage={errors?.title?.message} />
        </div>
        {isCompanySearch ? (
          <div className={clsx(sharedStyles.formField, styles.directorName)}>
            <DirectorSearch
              selectClassName={clsx(PRIVACY_FIELD_CLASS, {
                [sharedStyles.erroredField]:
                  errors?.firstName?.message || errors?.lastName?.message,
              })}
              onChange={handleDirectorSearchSelect}
              companyNumber={companyNumber}
              showCannotFindApplicantModal={false}
              label="Director's name"
            />
            {(errors?.firstName?.message || errors?.lastName?.message) && (
              <Message
                className={sharedStyles.errorMessage}
                message="Director is required"
                type="error"
              />
            )}
          </div>
        ) : (
          <>
            <div className={clsx(sharedStyles.formField, styles.firstName)}>
              <Input
                {...register('firstName')}
                id="firstName"
                type="text"
                label="First name"
                placeholder="John"
                className={clsx(PRIVACY_FIELD_CLASS, {
                  [sharedStyles.erroredField]: errors?.firstName?.message,
                })}
              />
              <FieldError errorMessage={errors?.firstName?.message} />
            </div>
            <div className={clsx(sharedStyles.formField, styles.lastName)}>
              <Input
                {...register('lastName')}
                id="lastName"
                type="text"
                label="Last name"
                placeholder="Smith"
                className={clsx(PRIVACY_FIELD_CLASS, {
                  [sharedStyles.erroredField]: errors?.lastName?.message,
                })}
              />
              <FieldError errorMessage={errors?.lastName?.message} />
            </div>
          </>
        )}
        <div className={sharedStyles.formField}>
          <Input
            {...register('email')}
            id="email"
            type="text"
            label="Email address"
            placeholder="me@iwoca.co.uk"
            className={clsx(PRIVACY_FIELD_CLASS, {
              [sharedStyles.erroredField]: errors?.email?.message,
            })}
          />
          <FieldError errorMessage={errors?.email?.message} />
        </div>
        <div className={sharedStyles.formField}>
          <Input
            {...register('phoneNumber')}
            id="phoneNumber"
            type="text"
            label="Phone number"
            placeholder="+44 123456789"
            className={clsx(PRIVACY_FIELD_CLASS, {
              [sharedStyles.erroredField]: errors?.phoneNumber?.message,
            })}
          />
          <FieldError errorMessage={errors?.phoneNumber?.message} />
        </div>
        <FileUpload
          docType="certified_id"
          label={DOCUMENT_UPLOAD_UK.certified_id.label}
          instruction={DOCUMENT_UPLOAD_UK.certified_id.instruction}
          selectedFiles={wrapFileInArray(watch('certifiedIdFile'))}
          errorMessage={errors?.certifiedIdFile?.message}
          onChange={(files) => setValue('certifiedIdFile', files[0])}
        />
      </Card>
    </div>
  );
};
